import type { AtprotoBlobRef } from "@brains/atproto-contracts";
import type { FetchLike } from "@brains/utils/fetch-like";
import { z } from "@brains/utils/zod";

export interface AtprotoSession {
  did: string;
  handle: string;
  accessJwt: string;
  refreshJwt: string;
}

export interface AtprotoPdsClientConfig {
  pdsEndpoint: string;
  identifier: string;
  appPassword: string;
  fetch?: FetchLike;
  requestTimeoutMs?: number;
}

// Publishing runs on boot and shutdown-drain paths; an unresponsive PDS must
// never hold either open indefinitely.
const DEFAULT_REQUEST_TIMEOUT_MS = 30_000;

function withRequestTimeout(fetchFn: FetchLike, timeoutMs: number): FetchLike {
  return (input, init) =>
    fetchFn(input, {
      ...init,
      signal: init?.signal
        ? AbortSignal.any([init.signal, AbortSignal.timeout(timeoutMs)])
        : AbortSignal.timeout(timeoutMs),
    });
}

export interface CreateRecordInput {
  repo: string;
  collection: string;
  record: Record<string, unknown>;
  rkey?: string;
  validate?: boolean;
}

export interface CreateRecordResult {
  uri: string;
  cid: string;
}

export interface PutRecordInput extends CreateRecordInput {
  rkey: string;
  swapRecord?: string;
}

export type PutRecordResult = CreateRecordResult;

export interface GetRecordInput {
  repo: string;
  collection: string;
  rkey: string;
}

export interface GetRecordResult {
  uri: string;
  cid: string;
  value: Record<string, unknown>;
}

export interface DeleteRecordInput {
  repo: string;
  collection: string;
  rkey: string;
}

export interface UploadBlobInput {
  data: Buffer;
  mimeType: string;
}

export interface UploadBlobResult {
  blob: AtprotoBlobRef;
}

const atprotoErrorResponseSchema = z.looseObject({
  message: z.string(),
});

const atprotoSessionSchema = z.looseObject({
  did: z.string(),
  handle: z.string(),
  accessJwt: z.string(),
  refreshJwt: z.string(),
});

const recordResultSchema = z.looseObject({
  uri: z.string(),
  cid: z.string(),
});

const getRecordResultSchema = recordResultSchema.extend({
  value: z.record(z.string(), z.unknown()),
});

const blobRefSchema = z
  .looseObject({
    $type: z.literal("blob").optional(),
    ref: z.looseObject({
      $link: z.string(),
    }),
    mimeType: z.string(),
    size: z.number().int().nonnegative(),
  })
  .transform((blob): AtprotoBlobRef => ({
    ...(blob.$type !== undefined ? { $type: blob.$type } : {}),
    ref: { $link: blob.ref.$link },
    mimeType: blob.mimeType,
    size: blob.size,
  }));

const uploadBlobResultSchema = z.looseObject({
  blob: blobRefSchema,
});

function trimEndpoint(endpoint: string): string {
  return endpoint.replace(/\/+$/, "");
}

const defaultFetch: FetchLike = (input, init): Promise<Response> =>
  fetch(input, init);

async function parseJsonResponse<T>(
  response: Response,
  schema: z.ZodType<T>,
): Promise<T> {
  const text = await response.text();
  const body = parseResponseBody(response, text);
  assertResponseOk(response, body);
  return schema.parse(body);
}

function parseResponseBody(response: Response, text: string): unknown {
  try {
    return text.length > 0 ? JSON.parse(text) : undefined;
  } catch {
    if (!response.ok) {
      throw new Error(`AT Protocol request failed with ${response.status}`);
    }
    throw new Error("Failed to parse JSON");
  }
}

function assertResponseOk(response: Response, body: unknown): void {
  if (response.ok) return;

  const error = atprotoErrorResponseSchema.safeParse(body);
  throw new Error(
    error.success
      ? error.data.message
      : `AT Protocol request failed with ${response.status}`,
  );
}

export class AtprotoPdsClient {
  private readonly pdsEndpoint: string;
  private readonly identifier: string;
  private readonly appPassword: string;
  private readonly fetchFn: FetchLike;
  private session?: AtprotoSession;

  constructor(config: AtprotoPdsClientConfig) {
    this.pdsEndpoint = trimEndpoint(config.pdsEndpoint);
    this.identifier = config.identifier;
    this.appPassword = config.appPassword;
    this.fetchFn = withRequestTimeout(
      config.fetch ?? defaultFetch,
      config.requestTimeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS,
    );
  }

  async createSession(): Promise<AtprotoSession> {
    const response = await this.fetchFn(
      `${this.pdsEndpoint}/xrpc/com.atproto.server.createSession`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          identifier: this.identifier,
          password: this.appPassword,
        }),
      },
    );

    const session = await parseJsonResponse(response, atprotoSessionSchema);
    this.session = session;
    return session;
  }

  async createRecord(input: CreateRecordInput): Promise<CreateRecordResult> {
    const session = await this.getSession();
    const response = await this.fetchFn(
      `${this.pdsEndpoint}/xrpc/com.atproto.repo.createRecord`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${session.accessJwt}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          repo: input.repo,
          collection: input.collection,
          record: input.record,
          ...(input.rkey && { rkey: input.rkey }),
          ...(input.validate !== undefined && { validate: input.validate }),
        }),
      },
    );

    return parseJsonResponse(response, recordResultSchema);
  }

  async putRecord(input: PutRecordInput): Promise<PutRecordResult> {
    const session = await this.getSession();
    const response = await this.fetchFn(
      `${this.pdsEndpoint}/xrpc/com.atproto.repo.putRecord`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${session.accessJwt}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          repo: input.repo,
          collection: input.collection,
          record: input.record,
          rkey: input.rkey,
          ...(input.validate !== undefined && { validate: input.validate }),
          ...(input.swapRecord !== undefined && {
            swapRecord: input.swapRecord,
          }),
        }),
      },
    );

    return parseJsonResponse(response, recordResultSchema);
  }

  async getRecord(input: GetRecordInput): Promise<GetRecordResult> {
    const params = new URLSearchParams({
      repo: input.repo,
      collection: input.collection,
      rkey: input.rkey,
    });
    const response = await this.fetchFn(
      `${this.pdsEndpoint}/xrpc/com.atproto.repo.getRecord?${params.toString()}`,
      { method: "GET" },
    );

    return parseJsonResponse(response, getRecordResultSchema);
  }

  async deleteRecord(input: DeleteRecordInput): Promise<void> {
    const session = await this.getSession();
    const response = await this.fetchFn(
      `${this.pdsEndpoint}/xrpc/com.atproto.repo.deleteRecord`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${session.accessJwt}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(input),
      },
    );
    const text = await response.text();
    const body = parseResponseBody(response, text);
    assertResponseOk(response, body);
  }

  async uploadBlob(input: UploadBlobInput): Promise<UploadBlobResult> {
    const session = await this.getSession();
    const response = await this.fetchFn(
      `${this.pdsEndpoint}/xrpc/com.atproto.repo.uploadBlob`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${session.accessJwt}`,
          "Content-Type": input.mimeType,
        },
        body: new Blob([new Uint8Array(input.data)], {
          type: input.mimeType,
        }),
      },
    );

    return parseJsonResponse(response, uploadBlobResultSchema);
  }

  private async getSession(): Promise<AtprotoSession> {
    this.session ??= await this.createSession();
    return this.session;
  }
}
